import { Badge } from '@synoptic/ui-kit/badge/badge.js';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@synoptic/ui-kit/tooltip/tooltip.js';
import { Control, useWatch } from 'react-hook-form';
import type { ListFormInputs } from '../list-form';
import {
  chipMode,
  chipValue,
  filterChip,
  overflowChip,
  summaryChips,
  tooltipChip,
  tooltipChipValue,
  tooltipChips,
} from './filters-summary.css';

const MAX_VISIBLE_CHIPS = 2;

type KeywordField = { mode: string; value: string };

const toFilledFields = (
  keywords?: ListFormInputs['filters']['includeKeywords'],
): KeywordField[] =>
  [
    { mode: 'All', value: keywords?.all },
    { mode: 'Any', value: keywords?.any },
    { mode: 'Phrase', value: keywords?.phrase },
  ]
    .map(({ mode, value }) => ({ mode, value: value?.trim() }))
    .filter((field): field is KeywordField => !!field.value);

const FilterChip = ({ mode, value }: KeywordField) => (
  <Badge color="neutral" className={filterChip}>
    <span className={chipMode}>{mode}</span>
    <span className={chipValue} title={value}>
      {value}
    </span>
  </Badge>
);

export const KeywordFiltersSummary = ({
  control,
  name,
}: {
  control: Control<ListFormInputs>;
  name: 'filters.includeKeywords' | 'filters.excludeKeywords';
}) => {
  const fields = toFilledFields(useWatch({ control, name }));

  if (fields.length === 0) {
    return null;
  }

  const visibleFields = fields.slice(0, MAX_VISIBLE_CHIPS);
  const hiddenFields = fields.slice(MAX_VISIBLE_CHIPS);

  return (
    <span className={summaryChips}>
      {visibleFields.map((field) => (
        <FilterChip key={field.mode} {...field} />
      ))}
      {hiddenFields.length > 0 && (
        <Tooltip delayDuration={100}>
          <TooltipTrigger asChild>
            <Badge color="neutral" className={overflowChip}>
              +{hiddenFields.length}
            </Badge>
          </TooltipTrigger>
          <TooltipContent
            render={() => (
              <span className={tooltipChips}>
                {hiddenFields.map((field) => (
                  <Badge
                    key={field.mode}
                    color="neutral"
                    className={tooltipChip}
                  >
                    <span className={chipMode}>{field.mode}</span>
                    <span className={tooltipChipValue}>{field.value}</span>
                  </Badge>
                ))}
              </span>
            )}
          />
        </Tooltip>
      )}
    </span>
  );
};

export const ActiveFiltersSummary = ({
  control,
}: {
  control: Control<ListFormInputs>;
}) => {
  const filters = useWatch({ control, name: 'filters' });
  const count =
    toFilledFields(filters?.includeKeywords).length +
    toFilledFields(filters?.excludeKeywords).length +
    (filters?.regex?.trim() ? 1 : 0) +
    (filters?.ai ? 1 : 0);

  if (count === 0) {
    return null;
  }

  return (
    <span className={summaryChips}>
      <Badge color="neutral" className={overflowChip}>
        {count} active
      </Badge>
    </span>
  );
};

export const RegexFilterSummary = ({
  control,
}: {
  control: Control<ListFormInputs>;
}) => {
  const regex = useWatch({ control, name: 'filters.regex' })?.trim();

  if (!regex) {
    return null;
  }

  return (
    <span className={summaryChips}>
      <Badge color="neutral" className={filterChip}>
        <span className={chipValue} title={regex}>
          {regex}
        </span>
      </Badge>
    </span>
  );
};
